import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {ButtonBehavior} from './button-behavior.js';
import {Sounds} from '../enums/Sounds.js';
import {Puzzle, PuzzleConstants} from './puzzle.js';
import {Statistics} from './statistics.js';
import {TransactionalNames} from '../enums/TransactionalNames.js';
import {Analytics, AnalyticsEvent} from '../classes/Analytics.js';
import {Coroutine} from '../classes/coroutines/Coroutine.js';
import {WLUtils} from '../utils/WLUtils.js';
import {waitForSeconds} from '../classes/coroutines/YieldInstructions.js';
import {AudioDirector} from './audio-director.js';
import {StatNames} from '../enums/StatNames.js';
import {BombPower} from './special-powers/bomb-power.js';

export class FruitSmasherButton extends ButtonBehavior {
    static TypeName = 'fruit-smasher-button';
    static InheritProperties = true;

    private static _instance: FruitSmasherButton;
    static get instance(): FruitSmasherButton {
        return FruitSmasherButton._instance;
    }

    init() {
        if (FruitSmasherButton._instance) {
            console.error('There can only be one instance of FruitSmasherButton Component');
        }
        FruitSmasherButton._instance = this;
    }

    @property.int(10)
    numberOfFruitCollected1: number = 10;
    @property.int(15)
    numberOfFruitCollected2: number = 15;
    @property.int(20)
    numberOfFruitCollected3: number = 20;
    @property.int(30)
    numberOfFruitCollected4: number = 30;
    @property.int(40)
    numberOfFruitCollected5: number = 40;

    @property.int(0)
    cooldownDecrease1: number = 0;
    @property.int(4)
    cooldownDecrease2: number = 4;
    @property.int(6)
    cooldownDecrease3: number = 6;
    @property.int(10)
    cooldownDecrease4: number = 10;
    @property.int(16)
    cooldownDecrease5: number = 16;

    //public fruitSmashFX: GameObject;
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.fruitSmasher)
    fruitSmashSFX: Sounds = Sounds.fruitSmasher;
    @property.float(0.4)
    timeBeforeSmashFX: number = 0.4;
    @property.float(1.5)
    hammerDownTime: number = 1.5;

    private _fruitSmasherHammer: Object3D;
    private _upgradedFruitCollected: number;
    private _numFruitSmashers: number;
    private _hammerSmashId: number;

    start(): void {
        super.start();
        this._numFruitSmashers = 0;

        this._fruitSmasherHammer = Puzzle.instance.score.fruitSmashHammer;

        const upgradeLevel = Statistics.instance.getTransactional(
            TransactionalNames.FruitSmasherUpgrade
        );

        switch (upgradeLevel) {
            case 1:
                this._upgradedFruitCollected = this.numberOfFruitCollected1;
                break;
            case 2:
                this._upgradedFruitCollected = this.numberOfFruitCollected2;
                break;
            case 3:
                this._upgradedFruitCollected = this.numberOfFruitCollected3;
                break;
            case 4:
                this._upgradedFruitCollected = this.numberOfFruitCollected4;
                break;
            case 5:
                this._upgradedFruitCollected = this.numberOfFruitCollected5;
                break;
            default:
                this._upgradedFruitCollected = 0;
                break;
        }
    }

    override buttonAction(): void {
        this._coroutineManager.stopCoroutine(this._hammerSmashId);
        this._hammerSmashId = this._coroutineManager.addCoroutine(
            new Coroutine(this._hammerSmash())
        );
        this._numFruitSmashers++;
    }

    protected override setCooldown(): number {
        const upgradeLevel = Statistics.instance.getTransactional(
            TransactionalNames.FruitSmasherUpgrade
        );
        let cooldown: number;

        switch (upgradeLevel) {
            case 1:
                cooldown = this.turnCooldown - this.cooldownDecrease1;
                break;
            case 2:
                cooldown = this.turnCooldown - this.cooldownDecrease2;
                break;
            case 3:
                cooldown = this.turnCooldown - this.cooldownDecrease3;
                break;
            case 4:
                cooldown = this.turnCooldown - this.cooldownDecrease4;
                break;
            case 5:
                cooldown = this.turnCooldown - this.cooldownDecrease5;
                break;
            default:
                cooldown = this.turnCooldown;
                break;
        }

        return cooldown < 1 ? 1 : cooldown;
    }

    private *_hammerSmash() {
        WLUtils.setActive(this._fruitSmasherHammer, true);
        yield* waitForSeconds(this.timeBeforeSmashFX);
        AudioDirector.instance.playOneShot(this.fruitSmashSFX, true);
        Statistics.instance.recordAdditive(StatNames.FruitSmashersUsed, 1);
        const puzzle = Puzzle.instance;
        let numPopped = 0;
        for (let x = PuzzleConstants.rowCount - 1; x >= 0; x--) {
            for (let y = 0; y < PuzzleConstants.columnCount; y++) {
                let index = puzzle.getObject(x, y);
                if (index != null) {
                    if (index.isFruit && !WLUtils.hasComponent(index.object, BombPower)) {
                        if (numPopped <= this._upgradedFruitCollected) {
                            puzzle.registerForPop(x, y, 0, 0);
                            numPopped++;
                        }
                    }
                }
            }
        }
        yield* waitForSeconds(this.hammerDownTime);
        WLUtils.setActive(this._fruitSmasherHammer, false);
    }

    override gameOver(puzzle: Puzzle) {
        super.gameOver(puzzle);

        Analytics.LogEventWithValue(
            AnalyticsEvent.FruitSmashersUsed,
            this._numFruitSmashers
        );
    }
}
